import { getLocalTimeZone, now, parseAbsolute } from "@internationalized/date"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import DoorAccessCodeCard from "@/globalComponents/molecules/DoorAccessCodeCard"
import PurchasedSingleSessionCard from "@/globalComponents/molecules/productCards/PurchasedSingleSessionCard"
import { useTheme } from "@/utilities/hooks/useTheme"
import { formatPhoneNumberForDisplay } from "@/utilities/phone"

interface ConfirmationSearchParams {
  contact: string
}

export const Route = createFileRoute(
  "/qr/$qrLookupID/passes/$epoch/redeem/$passID/confirmation"
)({
  staticData: {
    pageName: "Redemption Complete",
  },
  validateSearch: (
    search: Record<string, unknown>
  ): ConfirmationSearchParams => ({
    contact: (search.contact as string) || "",
  }),
  beforeLoad: async ({ context }) => {
    const receiptID = context.sessionState.receiptID || ""
    if (!receiptID) {
      throw new Error("Receipt ID is required")
    }

    const confirmationData = await api.receipt.POST({ receiptID })

    return { confirmationData }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { qrLookupID } = Route.useParams()
  const { confirmationData } = Route.useRouteContext()
  const { contact } = Route.useSearch()
  const { scannedArea } = useTheme()
  const { t } = useTranslation("park")
  const navigate = Route.useNavigate()

  // Format contact info - if it looks like a phone number, format it
  const contactInfo = contact.startsWith("+")
    ? formatPhoneNumberForDisplay(contact)
    : contact

  const locationName = scannedArea?.theme.locationInformation.locationName
  const sessionStartTime = parseAbsolute(
    confirmationData.receipt.startTime,
    getLocalTimeZone()
  )
  const sessionEndTime = parseAbsolute(
    confirmationData.receipt.endTime,
    getLocalTimeZone()
  )

  // Navigate the user to receipt once their session has ended
  useEffect(() => {
    const endtime = sessionEndTime
    const currentTime = now(getLocalTimeZone())
    const difference = endtime.compare(currentTime)

    const timeout = setTimeout(
      () => {
        navigate({
          to: "/qr/$qrLookupID/receipt/$receiptID",
          params: { qrLookupID, receiptID: confirmationData.receipt.receiptID },
          replace: true,
        })
      },
      Math.max(0, difference)
    )

    return () => clearTimeout(timeout)
  }, [navigate, qrLookupID, sessionEndTime, confirmationData.receipt])

  return (
    <>
      <Heading level={1} className="center">
        {t("confirmation.header")}
      </Heading>
      <Text level={1} className="center">
        {locationName
          ? t("confirmation.description.withLocation", {
              locationName,
            })
          : t("confirmation.description.withoutLocation")}
      </Text>
      <section className="module">
        {confirmationData.receipt.accessCode ? (
          <DoorAccessCodeCard>
            {confirmationData.receipt.accessCode}
          </DoorAccessCodeCard>
        ) : (
          <></>
        )}
        <PurchasedSingleSessionCard
          receiptID={confirmationData.receipt.receiptID}
          licensePlate={confirmationData.receipt.plate}
          sessionStartTime={sessionStartTime}
          sessionEndTime={sessionEndTime}
        />
        <Text
          className="center"
          style={{ width: "100%", overflowWrap: "break-word" }}
        >
          <Trans
            ns="receipt"
            i18nKey="sentNotification"
            values={{ contact: contactInfo }}
          >
            Your receipt has been sent to: <br />
            <em className="semi-bold" style={{ wordBreak: "break-word" }}>
              {contactInfo}
            </em>
          </Trans>
        </Text>
        <LinkButton
          buttonType="primary"
          presentation="filled"
          to="/qr/$qrLookupID/receipt/$receiptID"
          params={{ qrLookupID, receiptID: confirmationData.receipt.receiptID }}
        >
          <Icon name="receipt" slot="left" />
          {t("viewReceipt", { ns: "receipt" })}
        </LinkButton>
      </section>
      <Link
        to="/qr/$qrLookupID/welcome"
        params={{ qrLookupID }}
        className="center semi-bold body--2"
      >
        {t("common.returnToPurchaseMenu", { ns: "common" })}
      </Link>
    </>
  )
}
